/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import _ from 'lodash';

import { useDispatch } from 'react-redux';
import BottomBar from '../BottomBar';
import MultiSelectCheckbox from '../form/MultiSelectCheckbox';
import TextArea from '../form/TextArea';
import {
  bachelorDegrees,
  mastersDegrees,
  phds,
  medical,
  diplomas,
} from '../helpers';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { COUNTRY } from '../../../environment';
import {
  updateDegrees,
  updateAdditionalCertifications,
} from '../../../actions/preferences';

const ProfessionalDetails1 = ({
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  errorCallback,
  currentStep,
  previousPage,
  draftValues,
  onDraftChange,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    defaultValues: draftValues || {
      bachelor: [],
      masters: [],
      phd: [],
      medical: [],
      diploma: [],
      additional_degrees: '',
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const onSubmit = async values => {
    const errorMessage = formatMessage({
      defaultMessage: 'There was a problem updating your professional details.',
    });

    // Check if at least one degree is selected
    const allDegrees = [
      ..._.get(values, 'bachelor', []),
      ..._.get(values, 'masters', []),
      ..._.get(values, 'phd', []),
      ..._.get(values, 'medical', []),
      ..._.get(values, 'diploma', []),
    ];

    if (allDegrees.length === 0) {
      setError('degrees', {
        type: 'manual',
        message: formatMessage({
          defaultMessage: 'Must select at least one highest relevant degree',
        }),
      });
      return;
    }

    // Clear any existing degree validation errors
    clearErrors('degrees');

    const degreeValues = {
      degrees: allDegrees,
    };

    const additionalDegreeValues = {
      additional_degrees: {
        certifications:
          values.additional_degrees &&
          values.additional_degrees.split(',').map(v => v.trim()),
      },
      type_of: 'degrees',
    };

    openLoadingPanel(
      formatMessage({ defaultMessage: 'Updating professional details...' }),
    );

    try {
      await Promise.all([
        dispatch(updateDegrees(degreeValues)),
        dispatch(updateAdditionalCertifications(additionalDegreeValues)),
      ]);
      closeLoadingPanel();
      nextPage();
    } catch (error) {
      errorCallback(error, errorMessage);
    }
  };

  // Watch all degree fields to clear validation when user makes a selection
  const watchedDegrees = watch([
    'bachelor',
    'masters',
    'phd',
    'medical',
    'diploma',
  ]);

  // Clear the degrees error when user selects something
  useEffect(() => {
    const allEmpty = _.every(watchedDegrees, arr => _.isEmpty(arr));
    if (!allEmpty && errors.degrees) {
      clearErrors('degrees');
    }
  }, [watchedDegrees, errors.degrees, clearErrors]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>{formatMessage({ defaultMessage: 'Professional Details' })}</h1>
        <div className="sub-page-indicator">
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx, react/jsx-no-literals */}
          <span>1/{COUNTRY === 'US' ? '4' : '3'}</span>
        </div>
      </div>

      <h3 style={{ marginBottom: '35px' }}>
        {formatMessage({
          defaultMessage: 'Highest Relevant Degree (select all that apply)',
        })}
      </h3>

      <div className="input-row">
        <Controller
          name="bachelor"
          control={control}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              label={formatMessage({ defaultMessage: 'Bachelor' })}
              options={bachelorDegrees}
            />
          )}
        />
      </div>

      <div className="input-row">
        <Controller
          name="masters"
          control={control}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              label={formatMessage({ defaultMessage: 'Masters' })}
              options={mastersDegrees}
            />
          )}
        />
      </div>

      <div className="input-row">
        <Controller
          name="phd"
          control={control}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              label={formatMessage({ defaultMessage: 'PhD' })}
              options={phds}
            />
          )}
        />
      </div>

      <div className="input-row">
        <Controller
          name="medical"
          control={control}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              label={formatMessage({ defaultMessage: 'Medical' })}
              options={medical}
            />
          )}
        />
      </div>
      <div className="input-row">
        <Controller
          name="diploma"
          control={control}
          render={({ field }) => (
            <>
              <MultiSelectCheckbox
                {...field}
                label={formatMessage({ defaultMessage: 'Diploma/Certificate' })}
                options={diplomas}
              />
              {errors.degrees && (
                <p
                  className="onboarding-error"
                  style={{
                    marginTop: -35,
                  }}
                >
                  {errors.degrees.message}
                </p>
              )}
            </>
          )}
        />
      </div>
      <p className="onboarding-label">
        {formatMessage({
          defaultMessage: 'Other Degrees/Qualifications (separate by comma)',
        })}
      </p>
      <div className="input-row">
        <Controller
          name="additional_degrees"
          control={control}
          render={({ field }) => <TextArea {...field} />}
        />
      </div>

      <BottomBar
        previousPage={previousPage}
        nextPage={nextPage}
        currentStep={currentStep}
      />
    </form>
  );
};

ProfessionalDetails1.propTypes = {
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

export default ProfessionalDetails1;
